import React, { useState } from 'react';
import { Box, Chip, Collapse, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { RepeatStudyCheckResult } from '../rtk-apis/reports/repeatStudyCheck';

interface MedicareAlertBannerProps {
  result: RepeatStudyCheckResult | null;
}

const formatDate = (iso: string) => {
  if (!iso) {
    return '';
  }
  const [y, m, d] = iso.split('-');
  return d && m && y ? `${d}/${m}/${y}` : iso;
};

const typeLabel = (t: string) => (t === 'stressEcho' ? 'Stress Echo' : 'Echo (TTE)');

const AMBER = '#f7a35a';
const AMBER_INK = '#fbd7ae';

/**
 * Soft (dismissible, non-blocking) Medicare/MBS frequency-rule notification.
 * Shown when the patient has a prior echo / stress echo within the rolling window.
 */
const MedicareAlertBanner: React.FC<MedicareAlertBannerProps> = ({ result }) => {
  const [dismissed, setDismissed] = useState(false);

  if (!result || !result.isRepeat) {
    return null;
  }

  const hasStress = result.priorStudies.some(p => p.studyType === 'stressEcho');
  const overflow = result.priorStudies.length - 5;

  return (
    <Collapse in={!dismissed}>
      <Box
        role="alert"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          mx: 3,
          mt: 1,
          p: '12px 16px',
          borderRadius: '12px',
          border: `1px solid rgba(247, 163, 90, 0.45)`,
          background:
            'linear-gradient(92deg, rgba(247, 163, 90, 0.16) 0%, rgba(247, 163, 90, 0.05) 60%, rgba(247, 163, 90, 0.02) 100%)',
          backdropFilter: 'blur(4px)',
          boxShadow: '0 10px 28px -14px rgba(0, 0, 0, 0.55)',
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '10px',
            background: 'rgba(247, 163, 90, 0.18)',
            border: '1px solid rgba(247, 163, 90, 0.4)',
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 20, color: AMBER }} />
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{ fontSize: 13.5, fontWeight: 700, color: AMBER_INK, letterSpacing: 0.2 }}
          >
            Medicare — MBS frequency rule may apply
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: 'rgba(251, 215, 174, 0.75)', mt: 0.25 }}>
            Prior {hasStress ? 'echo / stress echo' : 'echo'} within {result.windowMonths} months of
            this study.
          </Typography>

          <Stack
            direction="row"
            flexWrap="wrap"
            gap={0.75}
            mt={1}
          >
            {result.priorStudies.slice(0, 5).map(p => (
              <Tooltip
                key={p.reportId}
                title={
                  p.matchedBy === 'name+dob' ? 'Matched by name + DOB' : 'Matched by patient ID'
                }
                arrow
              >
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${typeLabel(p.studyType)} · ${formatDate(p.studyDate)} · ${p.monthsAgo} mo ago`}
                  sx={{
                    color: AMBER_INK,
                    borderColor: 'rgba(247, 163, 90, 0.45)',
                    background: 'rgba(247, 163, 90, 0.08)',
                    fontSize: 12,
                    fontWeight: 600,
                    '& .MuiChip-label': { px: 1.1 },
                  }}
                />
              </Tooltip>
            ))}
            {overflow > 0 && (
              <Chip
                size="small"
                variant="outlined"
                label={`+${overflow} more`}
                sx={{
                  color: 'rgba(251, 215, 174, 0.7)',
                  borderColor: 'rgba(247, 163, 90, 0.3)',
                  background: 'transparent',
                  fontSize: 12,
                }}
              />
            )}
          </Stack>
        </Box>

        <IconButton
          size="small"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss Medicare alert"
          sx={{
            color: 'rgba(251, 215, 174, 0.65)',
            mt: '-2px',
            '&:hover': { color: AMBER_INK, background: 'rgba(247, 163, 90, 0.15)' },
          }}
        >
          <CloseRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Collapse>
  );
};

export default MedicareAlertBanner;
